import { useCallback, useEffect, useRef, useState } from 'react';

export const SPEED_OPTIONS = [1, 1.25, 1.5, 2, 0.5, 0.75];

/**
 * useVideoEngine — the core playback engine shared by every CCPlayer preset.
 * Subscribes to the <video> element's events and exposes state + controls.
 * Design doc: agent_docs/cel1157_tutorials_hub_design.md §5.1.
 *
 * The hook re-subscribes whenever the element mounts/changes (Theater renders
 * the element from first paint, so the ref is stable after mount; the
 * `elementKey` bump on error-retry forces a clean re-subscribe).
 */
const useVideoEngine = (videoRef, { src, onTimeUpdate } = {}) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [speed, setSpeedState] = useState(1);
  const prevVolumeRef = useRef(1);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;

  // Reset per-source state when the src changes.
  useEffect(() => {
    setError(false);
    setSpeedState(1);
    setCurrentTime(0);
    setDuration(0);
    setBuffered(0);
    const v = videoRef.current;
    if (v) v.playbackRate = 1;
  }, [src, videoRef]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;

    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    const handleTime = () => {
      setCurrentTime(v.currentTime);
      if (onTimeUpdateRef.current) onTimeUpdateRef.current(v.currentTime, v.duration || 0);
      if (v.buffered.length > 0) setBuffered(v.buffered.end(v.buffered.length - 1));
    };
    const handleMeta = () => {
      setDuration(v.duration || 0);
      setLoading(false);
      setError(false);
    };
    const handleError = () => { setError(true); setLoading(false); };
    const handleWaiting = () => setLoading(true);
    const handleCanPlay = () => setLoading(false);

    v.addEventListener('play', handlePlay);
    v.addEventListener('pause', handlePause);
    v.addEventListener('timeupdate', handleTime);
    v.addEventListener('loadedmetadata', handleMeta);
    v.addEventListener('durationchange', handleMeta);
    v.addEventListener('error', handleError);
    v.addEventListener('waiting', handleWaiting);
    v.addEventListener('canplay', handleCanPlay);
    if (v.duration) setDuration(v.duration);

    return () => {
      v.removeEventListener('play', handlePlay);
      v.removeEventListener('pause', handlePause);
      v.removeEventListener('timeupdate', handleTime);
      v.removeEventListener('loadedmetadata', handleMeta);
      v.removeEventListener('durationchange', handleMeta);
      v.removeEventListener('error', handleError);
      v.removeEventListener('waiting', handleWaiting);
      v.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoRef, src]);

  const play = useCallback(() => {
    const v = videoRef.current;
    if (v) v.play().catch(() => setError(true));
  }, [videoRef]);

  const pause = useCallback(() => {
    const v = videoRef.current;
    if (v) v.pause();
  }, [videoRef]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => setError(true));
    else v.pause();
  }, [videoRef]);

  /** Seek to an absolute time (clamped). `andPlay` starts playback — the
   *  transcript-chip contract: clicking a chip always results in playback. */
  const seekTo = useCallback((t, { andPlay = false } = {}) => {
    const v = videoRef.current;
    if (!v) return;
    const max = v.duration || duration || 0;
    v.currentTime = Math.max(0, Math.min(max || t, t));
    setCurrentTime(v.currentTime);
    if (andPlay && v.paused) v.play().catch(() => setError(true));
  }, [videoRef, duration]);

  const seekBy = useCallback((delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + delta));
    setCurrentTime(v.currentTime);
  }, [videoRef]);

  const setVolume = useCallback((value) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolumeState(value);
    if (value > 0) prevVolumeRef.current = value;
  }, [videoRef]);

  const toggleMute = useCallback(() => {
    setVolume(volume > 0 ? 0 : (prevVolumeRef.current || 1));
  }, [volume, setVolume]);

  const setSpeed = useCallback((value) => {
    const v = videoRef.current;
    if (v) v.playbackRate = value;
    setSpeedState(value);
  }, [videoRef]);

  /** Cycle through SPEED_OPTIONS; dir=-1 cycles backward (the `<` key). */
  const cycleSpeed = useCallback((dir = 1) => {
    setSpeedState((prev) => {
      const idx = SPEED_OPTIONS.indexOf(prev);
      const next = SPEED_OPTIONS[(idx + dir + SPEED_OPTIONS.length) % SPEED_OPTIONS.length];
      const v = videoRef.current;
      if (v) v.playbackRate = next;
      return next;
    });
  }, [videoRef]);

  const clearError = useCallback(() => setError(false), []);

  return {
    playing, currentTime, duration, buffered, loading, error, volume, speed,
    play, pause, togglePlay, seekTo, seekBy, setVolume, toggleMute, setSpeed, cycleSpeed, clearError,
  };
};

export default useVideoEngine;
